if (!customElements.get("td-image-zoom")) {
  /**
   * Image Zoom Web Component
   * Provides zoom and pan functionality for lifestyle images in product gallery
   * Integrates with Swiper to disable touch navigation when zoomed
   * Supports pinch-to-zoom on mobile devices with continuous zoom levels
   * Button controls use discrete levels (1x, 2x, 4x) while pinch allows continuous zoom
   */
  class TDImageZoom extends HTMLElement {
    constructor() {
      super();
      
      // Zoom configuration
      this.ZOOM_LEVELS = [1, 2, 4]; // Discrete levels for button controls
      this.MIN_ZOOM_SCALE = 1;
      this.MAX_ZOOM_SCALE = 4;
      
      // State management
      this.currentZoomScale = 1; // Continuous zoom level (used for pinch and rendering)
      this.currentButtonLevel = 0; // Current discrete level index for button controls
      this.panPosition = { x: 0, y: 0 };
      this.isDragging = false;
      this.lastPointer = { x: 0, y: 0 };
      
      // Pinch zoom state
      this.isPinching = false;
      this.initialPinchDistance = 0;
      this.initialPinchScale = 1;
      this.pinchCenter = { x: 0, y: 0 };
      
      // Elements
      this.imageElement = null;
      this.zoomContainer = null;
      this.zoomInBtn = null;
      this.zoomOutBtn = null;
      this.swiperInstance = null;
    }

    connectedCallback() {
      this.setupHTML();
      this.setupEventListeners();
      this.updateZoomControls();
      this.findSwiperInstance();
    }

    /**
     * Setup the HTML structure for zoom functionality
     */
    setupHTML() {
      // Wrap the existing image in a zoom container
      this.imageElement = this.querySelector('img');
      if (!this.imageElement) return;

      // Create zoom container
      this.zoomContainer = document.createElement('div');
      this.zoomContainer.className = 'td-zoom__container';
      
      // Wrap the image
      this.imageElement.parentNode.insertBefore(this.zoomContainer, this.imageElement);
      this.zoomContainer.appendChild(this.imageElement);

      // Create zoom controls
      const controlsHTML = `
        <div class="td-zoom__controls">
          <button class="td-zoom__btn td-zoom__btn--in" type="button" aria-label="Zoom in">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.2-5.2m0 0A7.5 7.5 0 105.2 5.2a7.5 7.5 0 0010.6 10.6zM10.5 7.5v6m3-3h-6"></path></svg>
          </button>
          <button class="td-zoom__btn td-zoom__btn--out" type="button" aria-label="Zoom out">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.2-5.2m0 0A7.5 7.5 0 105.2 5.2a7.5 7.5 0 0010.6 10.6zM13.5 10.5h-6"></path></svg>
          </button>
        </div>
      `;

      this.insertAdjacentHTML('beforeend', controlsHTML);
      
      // Get control references
      this.zoomInBtn = this.querySelector('.td-zoom__btn--in');
      this.zoomOutBtn = this.querySelector('.td-zoom__btn--out');

      // Add CSS styles
      this.addStyles();
    }

    /**
     * Add CSS styles for zoom functionality
     */
    addStyles() {
      if (document.querySelector('#td-image-zoom-styles')) return;

      const style = document.createElement('style');
      style.id = 'td-image-zoom-styles';
      style.textContent = `
        td-image-zoom {
          position: relative;
          display: block;
          overflow: hidden;
        }

        .td-zoom__container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          cursor: grab;
        }

        .td-zoom__container.td-zoom__container--dragging {
          cursor: grabbing;
        }

        .td-zoom__container img {
          transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          transform-origin: center center;
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .td-zoom__controls {
          position: absolute;
          top: 90px;
          right: 15px;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 10;
        }

        .td-zoom__btn {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: #e7edf1;
          color: #0009;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          backdrop-filter: blur(4px);
          outline: none!important;
        }

        .td-zoom__btn:hover:not(:disabled) {
          background:   #d7e0e5;
        }

        .td-zoom__btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          outline: none;
        }

        .td-zoom__btn svg {
          width: 24px;
          height: 24px;
        }

        /* Mobile specific styles */
        @media (min-width: 768px) {
          .td-zoom__controls {
            // right: 10px;
            top: 50px;
          }
        }

        @media (min-width: 1024px) {
          .td-zoom__controls {
            top: 60px;
          }
        }
      `;
      
      document.head.appendChild(style);
    }

    /**
     * Setup event listeners for zoom and pan functionality
     */
    setupEventListeners() {
      if (!this.zoomInBtn || !this.zoomOutBtn || !this.zoomContainer) return;

      // Zoom controls
      this.zoomInBtn.addEventListener('click', () => this.zoomIn());
      this.zoomOutBtn.addEventListener('click', () => this.zoomOut());

      // Pan functionality - Mouse events
      this.zoomContainer.addEventListener('mousedown', (e) => this.startPan(e));
      document.addEventListener('mousemove', (e) => this.panImage(e));
      document.addEventListener('mouseup', () => this.endPan());

      // Pan and pinch functionality - Touch events  
      this.zoomContainer.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
      document.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
      document.addEventListener('touchend', (e) => this.handleTouchEnd(e));

      // Prevent context menu on long press
      this.zoomContainer.addEventListener('contextmenu', (e) => {
        if (this.currentZoomScale > 1) {
          e.preventDefault();
        }
      });
    }

    /**
     * Handle touch start - detect single touch or pinch
     */
    handleTouchStart(event) {
      if (event.touches.length === 1) {
        // Single touch - start pan
        this.startPan(event);
      } else if (event.touches.length === 2) {
        // Two fingers - start pinch
        this.startPinch(event);
      }
    }

    /**
     * Handle touch move - pan or pinch
     */
    handleTouchMove(event) {
      if (event.touches.length === 1 && this.isDragging && !this.isPinching) {
        // Single touch pan
        this.panImage(event);
      } else if (event.touches.length === 2 && this.isPinching) {
        // Two finger pinch
        this.handlePinch(event);
      }
    }

    /**
     * Handle touch end - end pan or pinch
     */
    handleTouchEnd(event) {
      if (event.touches.length === 0) {
        // All fingers lifted
        this.endPan();
        this.endPinch();
      } else if (event.touches.length === 1 && this.isPinching) {
        // One finger lifted during pinch, switch to pan
        this.endPinch();
        this.startPan(event);
      }
    }

    /**
     * Start pinch gesture
     */
    startPinch(event) {
      if (event.touches.length !== 2) return;

      this.isPinching = true;
      this.isDragging = false; // Stop panning during pinch
      
      // Calculate initial distance between fingers
      this.initialPinchDistance = this.calculatePinchDistance(event.touches);
      this.initialPinchScale = this.currentZoomScale;
      
      // Calculate pinch center point relative to container
      const containerRect = this.zoomContainer.getBoundingClientRect();
      this.pinchCenter = {
        x: ((event.touches[0].clientX + event.touches[1].clientX) / 2) - containerRect.left,
        y: ((event.touches[0].clientY + event.touches[1].clientY) / 2) - containerRect.top
      };

      event.preventDefault();
    }

    /**
     * Handle pinch gesture
     */
    handlePinch(event) {
      if (!this.isPinching || event.touches.length !== 2) return;

      const currentDistance = this.calculatePinchDistance(event.touches);
      const pinchRatio = currentDistance / this.initialPinchDistance;
      
      // Calculate new zoom scale
      let newScale = this.initialPinchScale * pinchRatio;
      newScale = Math.max(this.MIN_ZOOM_SCALE, Math.min(this.MAX_ZOOM_SCALE, newScale));
      
      // Update zoom scale and apply transformation
      this.currentZoomScale = newScale;
      this.applyZoom();
      this.updateZoomControls();
      this.updateSwiperTouchState();

      event.preventDefault();
    }

    /**
     * End pinch gesture
     */
    endPinch() {
      if (!this.isPinching) return;

      this.isPinching = false;
      
      // Update button level to match current continuous zoom scale
      this.syncButtonLevelToCurrentScale();
      
      // Reset pan position when returning to original size
      if (this.currentZoomScale === 1) {
        this.panPosition = { x: 0, y: 0 };
        this.applyZoom();
      }
    }

    /**
     * Calculate distance between two touch points
     */
    calculatePinchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Sync button level to current continuous zoom scale
     * Finds the closest discrete level to the current scale
     */
    syncButtonLevelToCurrentScale() {
      let closestLevel = 0;
      let minDifference = Math.abs(this.currentZoomScale - this.ZOOM_LEVELS[0]);

      for (let i = 1; i < this.ZOOM_LEVELS.length; i++) {
        const difference = Math.abs(this.currentZoomScale - this.ZOOM_LEVELS[i]);
        if (difference < minDifference) {
          minDifference = difference;
          closestLevel = i;
        }
      }

      this.currentButtonLevel = closestLevel;
      this.updateZoomControls();
    }

    /**
     * Find the parent Swiper instance for touch integration
     */
    findSwiperInstance() {
      const swiperElement = this.closest('[data-swiper]');
      if (swiperElement && swiperElement.swiper) {
        this.swiperInstance = swiperElement.swiper;
      }
    }

    /**
     * Zoom in to next discrete level
     */
    zoomIn() {
      if (this.currentButtonLevel < this.ZOOM_LEVELS.length - 1) {
        this.currentButtonLevel++;
        this.currentZoomScale = this.ZOOM_LEVELS[this.currentButtonLevel];
        this.applyZoom();
        this.updateZoomControls();
        this.updateSwiperTouchState();
      }
    }

    /**
     * Zoom out to previous discrete level
     */
    zoomOut() {
      if (this.currentButtonLevel > 0) {
        this.currentButtonLevel--;
        this.currentZoomScale = this.ZOOM_LEVELS[this.currentButtonLevel];
        
        // Reset pan position when returning to original size
        if (this.currentZoomScale === 1) {
          this.panPosition = { x: 0, y: 0 };
        }
        
        this.applyZoom();
        this.updateZoomControls();
        this.updateSwiperTouchState();
      }
    }

    /**
     * Apply zoom transformation to the image
     */
    applyZoom() {
      if (!this.imageElement) return;

      const transform = `scale(${this.currentZoomScale}, ${this.currentZoomScale}) translate(${this.panPosition.x}px, ${this.panPosition.y}px)`;
      
      this.imageElement.style.transform = transform;
      
      // Add/remove zoomed class based on zoom scale
      if (this.currentZoomScale > 1) {
        this.classList.add('td-image-zoomed');
        this.dispatchEvent(new CustomEvent('td-image-zoomed', { bubbles: true }));
      } else {
        this.classList.remove('td-image-zoomed');
        this.dispatchEvent(new CustomEvent('td-image-zoom-end', { bubbles: true }));
      }
    }


    /**
     * Update zoom control button states based on current zoom scale
     */
    updateZoomControls() {
      if (!this.zoomInBtn || !this.zoomOutBtn) return;

      // Always use continuous scale - only disable at actual min/max thresholds
      this.zoomInBtn.disabled = this.currentZoomScale >= this.MAX_ZOOM_SCALE;
      this.zoomOutBtn.disabled = this.currentZoomScale <= this.MIN_ZOOM_SCALE;
    }

    /**
     * Update Swiper touch state based on zoom level
     */
    updateSwiperTouchState() {
      if (!this.swiperInstance) return;

      // Disable swiper touch when zoomed on mobile
      if (window.innerWidth <= 1024) {
        if (this.currentZoomScale > 1) {
          this.swiperInstance.allowTouchMove = false;
        } else {
          this.swiperInstance.allowTouchMove = true;
        }
      }
    }

    /**
     * Start pan operation
     */
    startPan(event) {
      if (this.currentZoomScale === 1 || this.isPinching) return;

      this.isDragging = true;
      this.zoomContainer.classList.add('td-zoom__container--dragging');

      const pointer = this.getPointerPosition(event);
      this.lastPointer = { x: pointer.x, y: pointer.y };

      // Prevent default to avoid conflicts with swiper
      event.preventDefault();
    }

    /**
     * Pan the image based on pointer movement
     */
    panImage(event) {
      if (!this.isDragging || this.currentZoomScale === 1 || this.isPinching) return;

      const pointer = this.getPointerPosition(event);
      const deltaX = pointer.x - this.lastPointer.x;
      const deltaY = pointer.y - this.lastPointer.y;

      // Calculate new pan position with bounds checking
      const newPanX = this.panPosition.x + deltaX / this.currentZoomScale;
      const newPanY = this.panPosition.y + deltaY / this.currentZoomScale;

      // Apply bounds to prevent panning too far
      const bounds = this.calculatePanBounds();
      this.panPosition.x = Math.max(bounds.minX, Math.min(bounds.maxX, newPanX));
      this.panPosition.y = Math.max(bounds.minY, Math.min(bounds.maxY, newPanY));

      this.lastPointer = { x: pointer.x, y: pointer.y };
      this.applyZoom();

      // Prevent default to avoid scrolling and swiper conflicts
      event.preventDefault();
    }

    /**
     * End pan operation
     */
    endPan() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.zoomContainer.classList.remove('td-zoom__container--dragging');
    }

    /**
     * Calculate pan boundaries to keep image visible
     */
    calculatePanBounds() {
      if (!this.zoomContainer || !this.imageElement) {
        return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
      }

      const containerRect = this.zoomContainer.getBoundingClientRect();
      const scale = this.currentZoomScale;
      
      // Calculate how much the scaled image extends beyond the container
      const overflow = (scale - 1) / 2;
      const maxPanX = containerRect.width * overflow / scale;
      const maxPanY = containerRect.height * overflow / scale;

      return {
        minX: -maxPanX,
        maxX: maxPanX,
        minY: -maxPanY,
        maxY: maxPanY
      };
    }

    /**
     * Get pointer position from mouse or touch event
     */
    getPointerPosition(event) {
      if (event.touches && event.touches.length > 0) {
        return {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
      }
      return {
        x: event.clientX,
        y: event.clientY
      };
    }

    /**
     * Reset zoom to original state
     */
    resetZoom() {
      this.currentZoomScale = 1;
      this.currentButtonLevel = 0;
      this.panPosition = { x: 0, y: 0 };
      this.applyZoom();
      this.updateZoomControls();
      this.updateSwiperTouchState();
    }

    /**
     * Public API method to check if image is zoomed
     */
    isZoomed() {
      return this.currentZoomScale > 1;
    }
  }

  customElements.define("td-image-zoom", TDImageZoom);
} 